import { GetSurveyRuleValueOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleValueOptionsRPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  AutoCompleteItem,
  MultiAutoCompleteInputField,
  TextInputField,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import {
  processSurveyParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { SurveyRuleConditionModel } from "../../models/SurveyRuleConditionModel";
import { useGetSurveyRuleValueOptionsStore } from "../../stores/Hooks";
import { getOptionsWithSelectedAutoCompleteValues } from "../../utils/getOptionsWithSelectedAutoCompleteValues";
import { getConditionValueValidationErrorHelperText } from "../../utils/getSurveyRuleValidationErrorHelperText";
import { Code } from "@pulse/pulse-rpcs/lib/types/code";
import { RespondentColumnName } from "@pulse/pulse-rpcs/lib/types/respondentColumnName";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";

interface SurveyRuleValueInputFieldProps {
  onChange: (selectedItems: AutoCompleteItem[]) => void;
  onTextChange: (value: string) => void;
  surveyRuleCondition: Instance<typeof SurveyRuleConditionModel>;
}

export const SOURCE_VALUE_FIELD_WIDTH = "280px";

export const SurveyRuleValueInputField: React.FC<SurveyRuleValueInputFieldProps> =
  observer(({ onChange, onTextChange, surveyRuleCondition }) => {
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);
    const [commaSeparatedSourceValue, setCommaSeparatedSourceValue] = useState(
      surveyRuleCondition.keyType.conditionValue.join(", "),
    );

    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();
    const sourceValueFieldId = useId();

    const getSurveyRuleValueOptionsStore = useGetSurveyRuleValueOptionsStore();

    const conditionValues = Array.from(
      surveyRuleCondition.keyType.conditionValue,
    );
    const valueOptionsRequestKey = [
      surveyRuleCondition.conditionId,
      surveyRuleCondition.keyType.type,
      surveyRuleCondition.keyType.conditionKey ?? EMPTY_CHARACTER,
    ].join(":");
    const valueOption = getSurveyRuleValueOptionsStore.valueOption.get(
      valueOptionsRequestKey,
    );

    const getSourceValueOptions = (): string[] => {
      switch (valueOption?.type) {
        case undefined:
          return [];
        case "QUESTION_OPTION_CODE":
          return Array.from(valueOption.questionOptionCodes);
        case "RESPONDENT_COLUMN_VALUE":
          switch (valueOption.valueType.type) {
            case "UNIQUE_VALUES":
              return valueOption.valueType.values;
            case "TOO_MANY_UNIQUE_VALUES":
              return [];
            default:
              return valueOption.valueType satisfies never;
          }
        default:
          return valueOption satisfies never;
      }
    };
    const sourceValueOptions = getSourceValueOptions().map((value) => ({
      id: value,
      label: value,
    }));

    const selectedConditionValues = conditionValues.map((value) => ({
      id: value,
      label: value,
    }));
    const sourceValueOptionsWithSelectedValues =
      getOptionsWithSelectedAutoCompleteValues(
        sourceValueOptions,
        selectedConditionValues,
      );
    const isTooManyUniqueSourceValue =
      valueOption?.type === "RESPONDENT_COLUMN_VALUE" &&
      valueOption.valueType.type === "TOO_MANY_UNIQUE_VALUES";
    const isSourceValueLoading =
      getSurveyRuleValueOptionsStore.loadingGetSurveyRuleValueOptions.get(
        valueOptionsRequestKey,
      ) ?? false;
    const sourceValueValidationHelperText =
      getConditionValueValidationErrorHelperText(
        t,
        surveyRuleCondition.keyType.conditionValueValidationError,
      );

    const getValueOptionsRequestValue = (
      surveyRuleKey: string,
    ): GetSurveyRuleValueOptionsRPC.RequestEnums.Key.Key => {
      switch (surveyRuleCondition.keyType.type) {
        case "QUESTION":
          return new GetSurveyRuleValueOptionsRPC.RequestEnums.Key.QuestionCode(
            new Code(surveyRuleKey),
          );
        case "RESPONDENT":
          return new GetSurveyRuleValueOptionsRPC.RequestEnums.Key.RespondentColumn(
            new RespondentColumnName(surveyRuleKey),
          );
        default:
          return surveyRuleCondition.keyType satisfies never;
      }
    };

    useEffect(() => {
      setCommaSeparatedSourceValue(conditionValues.join(", "));
    }, [
      surveyRuleCondition.conditionId,
      surveyRuleCondition.keyType.conditionKey,
      isTooManyUniqueSourceValue,
    ]);

    useEffect(() => {
      const sourceValueFieldDOMElement =
        document.getElementById(sourceValueFieldId);
      if (!sourceValueFieldDOMElement) return;

      const handleSourceValueFieldFocus = async (): Promise<void> => {
        const conditionKey = surveyRuleCondition.keyType.conditionKey;
        if (!conditionKey) return;

        await processSurveyParams(async (surveyId) => {
          const rpcError = await getSurveyRuleValueOptionsStore
            .getSurveyRuleValueOptions(
              surveyId,
              getValueOptionsRequestValue(conditionKey),
              valueOptionsRequestKey,
            )
            .catch(() => {
              setOpenUnexpectedErrorDialog(true);
              return undefined;
            });

          switch (rpcError) {
            case undefined:
              surveyRuleCondition.keyType.clearConditionKeyValidationErrors();
              break;
            case GetSurveyRuleValueOptionsRPC.RPCError.InvalidSurveyId:
              navigateTo.pageNotFound();
              break;
            case GetSurveyRuleValueOptionsRPC.RPCError.InvalidQuestionCode:
              if (surveyRuleCondition.keyType.type === "QUESTION") {
                surveyRuleCondition.keyType.setConditionKeyValidationError(
                  SurveyRuleConditionValidationError
                    .SurveyRuleConditionValidationError.INVALID_QUESTION_KEY,
                );
              }
              break;
            case GetSurveyRuleValueOptionsRPC.RPCError
              .InvalidRespondentColumnName:
              if (surveyRuleCondition.keyType.type === "RESPONDENT") {
                surveyRuleCondition.keyType.setConditionKeyValidationError(
                  SurveyRuleConditionValidationError
                    .SurveyRuleConditionValidationError.INVALID_RESPONDENT_KEY,
                );
              }
              break;
            case GetSurveyRuleValueOptionsRPC.RPCError.UnsupportedQuestionType:
              if (surveyRuleCondition.keyType.type === "QUESTION") {
                surveyRuleCondition.keyType.setConditionKeyValidationError(
                  SurveyRuleConditionValidationError
                    .SurveyRuleConditionValidationError.INVALID_QUESTION_TYPE,
                );
              }
              break;
            default:
              rpcError satisfies never;
          }
        }, navigateTo);
      };

      sourceValueFieldDOMElement.addEventListener(
        "focus",
        handleSourceValueFieldFocus,
      );

      return () =>
        sourceValueFieldDOMElement.removeEventListener(
          "focus",
          handleSourceValueFieldFocus,
        );
    }, [
      getSurveyRuleValueOptionsStore,
      surveyRuleCondition.keyType.type,
      navigateTo,
      surveyRuleCondition.keyType.conditionKey,
      sourceValueFieldId,
      valueOptionsRequestKey,
    ]);

    const handleCommaSeparatedSourceValueChange = (value: string): void => {
      setCommaSeparatedSourceValue(value);
      onTextChange(value);
    };

    return (
      <>
        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={t("common.uploadDialog.unexpectedErrorDescription")}
            onClickDone={() => setOpenUnexpectedErrorDialog(false)}
          />
        )}
        {isTooManyUniqueSourceValue ? (
          <TextInputField
            name="surveyRuleSourceValue"
            label={EMPTY_CHARACTER}
            type="text"
            placeholder={t("surveys.rules.sourceValue")}
            value={commaSeparatedSourceValue}
            onTextChange={handleCommaSeparatedSourceValueChange}
            helperText={
              sourceValueValidationHelperText ??
              t("surveys.rules.commaSeparatedValueHelperText")
            }
            error={
              surveyRuleCondition.keyType.conditionValueValidationError.length >
              0
            }
            disabled={
              !surveyRuleCondition.keyType.conditionKey || isSourceValueLoading
            }
            width={SOURCE_VALUE_FIELD_WIDTH}
            minWidth={SOURCE_VALUE_FIELD_WIDTH}
          />
        ) : (
          <MultiAutoCompleteInputField
            id={sourceValueFieldId}
            label={EMPTY_CHARACTER}
            placeholder={t("surveys.rules.sourceValue")}
            options={sourceValueOptionsWithSelectedValues}
            value={selectedConditionValues}
            onChange={onChange}
            selectType="checkbox"
            isLoading={isSourceValueLoading}
            loadingText={t("surveys.rules.loadingValueOptionsText")}
            noOptionsText={t("surveys.autoCompleteField.noResultsFound")}
            error={
              surveyRuleCondition.keyType.conditionValueValidationError.length >
              0
            }
            helperText={sourceValueValidationHelperText}
            isDisabled={!surveyRuleCondition.keyType.conditionKey}
            width={SOURCE_VALUE_FIELD_WIDTH}
            minWidth={SOURCE_VALUE_FIELD_WIDTH}
          />
        )}
      </>
    );
  });
